import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Analytics,
  TextFields,
  Timer,
  TrendingUp,
  Visibility,
  School,
  Psychology,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const TextAnalytics = ({ analytics, theme }) => {
  // Get readability level description
  const getReadabilityLevel = (score) => {
    if (score >= 90) return { level: 'Very Easy', color: theme.success };
    if (score >= 80) return { level: 'Easy', color: theme.success };
    if (score >= 70) return { level: 'Fairly Easy', color: '#10b981' };
    if (score >= 60) return { level: 'Standard', color: theme.warning };
    if (score >= 50) return { level: 'Fairly Difficult', color: '#f59e0b' };
    if (score >= 30) return { level: 'Difficult', color: theme.error };
    return { level: 'Very Difficult', color: theme.error };
  };

  // Calculate estimated reading time (average 200 words per minute)
  const getReadingTime = (wordCount) => {
    const minutes = Math.ceil(wordCount / 200);
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  };

  // Get average words per sentence
  const getAvgWordsPerSentence = () => {
    if (analytics.sentenceCount === 0) return 0;
    return Math.round(analytics.wordCount / analytics.sentenceCount);
  };

  // Get writing complexity score
  const getComplexityScore = () => {
    const avgWordsPerSentence = getAvgWordsPerSentence();
    if (avgWordsPerSentence <= 15) return { level: 'Simple', color: theme.success, score: 85 };
    if (avgWordsPerSentence <= 20) return { level: 'Moderate', color: theme.warning, score: 65 };
    return { level: 'Complex', color: theme.error, score: 35 };
  };

  const readability = getReadabilityLevel(analytics.readabilityScore);
  const complexity = getComplexityScore();

  const analyticsCards = [
    {
      title: 'Word Count',
      value: analytics.wordCount.toLocaleString(),
      icon: <TextFields />,
      color: theme.primary,
      subtitle: `${analytics.characterCount.toLocaleString()} characters`
    },
    {
      title: 'Sentences',
      value: analytics.sentenceCount,
      icon: <Analytics />,
      color: '#10b981',
      subtitle: `${getAvgWordsPerSentence()} avg words/sentence`
    },
    {
      title: 'Reading Time',
      value: getReadingTime(analytics.wordCount),
      icon: <Timer />,
      color: '#8b5cf6',
      subtitle: 'Estimated time'
    },
    {
      title: 'AI Suggestions',
      value: analytics.suggestionCount,
      icon: <Psychology />,
      color: '#f59e0b',
      subtitle: 'Improvements found'
    }
  ];

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: theme.surface,
        borderRadius: 2
      }}
    >
      <Box display="flex" alignItems="center" mb={3}>
        <Analytics sx={{ mr: 1, color: theme.primary }} />
        <Typography variant="h6" color={theme.text}>
          Text Analytics
        </Typography>
        <Chip
          label={analytics.analysisType}
          size="small"
          sx={{
            ml: 2,
            backgroundColor: theme.primary,
            color: 'white',
            textTransform: 'capitalize'
          }}
        />
      </Box>

      {/* Analytics Cards */}
      <Grid container spacing={3} mb={3}>
        {analyticsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  backgroundColor: theme.background,
                  border: `1px solid ${theme.textSecondary}20`,
                  height: '100%'
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Box
                      sx={{
                        color: card.color,
                        mr: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="subtitle2" color={theme.textSecondary}>
                      {card.title}
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color={theme.text}
                    mb={0.5}
                  >
                    {card.value}
                  </Typography>
                  
                  <Typography variant="caption" color={theme.textSecondary}>
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Readability and Complexity Scores */}
      <Grid container spacing={3}>
        {/* Readability Score */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card
              sx={{
                backgroundColor: theme.background,
                border: `1px solid ${theme.textSecondary}20`,
                height: '100%'
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Visibility sx={{ mr: 1, color: readability.color }} />
                  <Typography variant="subtitle1" color={theme.text} fontWeight="bold">
                    Readability Score
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h3" fontWeight="bold" color={readability.color}>
                    {analytics.readabilityScore}
                  </Typography>
                  <Chip
                    label={readability.level}
                    sx={{
                      backgroundColor: readability.color,
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                
                <LinearProgress
                  variant="determinate"
                  value={analytics.readabilityScore}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${theme.textSecondary}20`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: readability.color,
                      borderRadius: 4
                    }
                  }}
                />
                
                <Typography
                  variant="caption"
                  color={theme.textSecondary}
                  sx={{ mt: 1, display: 'block' }}
                >
                  Based on Flesch Reading Ease formula
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Writing Complexity */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Card
              sx={{
                backgroundColor: theme.background,
                border: `1px solid ${theme.textSecondary}20`,
                height: '100%'
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <School sx={{ mr: 1, color: complexity.color }} />
                  <Typography variant="subtitle1" color={theme.text} fontWeight="bold">
                    Writing Complexity
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="h3" fontWeight="bold" color={complexity.color}>
                    {complexity.score}
                  </Typography>
                  <Chip
                    label={complexity.level}
                    sx={{
                      backgroundColor: complexity.color,
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                
                <LinearProgress
                  variant="determinate"
                  value={complexity.score}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${theme.textSecondary}20`,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: complexity.color,
                      borderRadius: 4
                    }
                  }}
                />
                
                <Typography
                  variant="caption"
                  color={theme.textSecondary}
                  sx={{ mt: 1, display: 'block' }}
                >
                  Based on sentence length and structure
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Quick Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <Box mt={3}>
          <Typography variant="subtitle1" color={theme.text} fontWeight="bold" mb={2}>
            <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
            Quick Insights
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={1}>
            {analytics.wordCount > 500 && (
              <Chip
                icon={<CheckCircle />}
                label="Substantial content"
                size="small"
                sx={{ backgroundColor: theme.success, color: 'white' }}
              />
            )}
            
            {analytics.readabilityScore >= 70 && (
              <Chip
                icon={<CheckCircle />}
                label="Easy to read"
                size="small"
                sx={{ backgroundColor: theme.success, color: 'white' }}
              />
            )}
            
            {getAvgWordsPerSentence() <= 20 && (
              <Chip
                icon={<CheckCircle />}
                label="Good sentence length"
                size="small"
                sx={{ backgroundColor: theme.success, color: 'white' }}
              />
            )}
            
            {analytics.suggestionCount === 0 && (
              <Chip
                icon={<CheckCircle />}
                label="No issues found"
                size="small"
                sx={{ backgroundColor: theme.success, color: 'white' }}
              />
            )}
            
            {analytics.suggestionCount > 0 && (
              <Chip
                label={`${analytics.suggestionCount} improvements available`}
                size="small"
                sx={{ backgroundColor: theme.warning, color: 'white' }}
              />
            )}
          </Box>
        </Box>
      </motion.div>
    </Paper>
  );
};

export default TextAnalytics;
